import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import toast from "react-hot-toast";
import {
  CalendarClock,
  Image as ImageIcon,
  Languages,
  Link2,
  Target,
  X,
} from "lucide-react";
import {
  Alert,
  Button,
  ConfirmModal,
  FieldError,
  Input,
  Label,
  MultiSelect,
  Switch,
  type MultiSelectOption,
} from "@/components/ui";
import {
  CatalogTranslatedField,
  MediaUpload,
  PickerPanel,
  QueryLoadError,
  SaveBar,
  SectionCard,
} from "@/components/common";
import { toastApiError } from "@/lib/errorMessages";
import {
  SUPPORTED_LOCALES,
  resolveLocale,
  type SupportedLocale,
} from "@/lib/locales";
import { matchesTextQuery } from "@/lib/textSearch";
import { queryLoadFailure } from "@/lib/queryLoadFailure";
import {
  findAutoTranslateSourceLanguage,
  resolveCatalogTranslation,
} from "@/lib/translationResolution";
import { adminService } from "@/services/adminService";
import {
  carouselSlideKeys,
  carouselSlideService,
  type CarouselSlide,
} from "@/services/carouselSlideService";
import {
  applyAutoTranslateResult,
  useCatalogAutoTranslate,
} from "@/hooks/useCatalogAutoTranslate";
import { useUnsavedFormGuard } from "@/hooks/useUnsavedFormGuard";
import {
  CAROUSEL_SLIDE_DESCRIPTION_MAX_LENGTH,
  CAROUSEL_SLIDE_IMAGE_ASPECT,
  CAROUSEL_SLIDE_TITLE_MAX_LENGTH,
} from "@/constants/carouselSlideMedia";
import { ACTIVITY_TYPE_ORDER, activityTypeLabel } from "@/constants";
import type { ActivityCategory } from "@/types";
import {
  CAROUSEL_SLIDE_FORM_ID,
  emptyFormState,
  fieldsFromForm,
  formFromSlide,
  serializeFormState,
  validateSlideForm,
  type FormErrors,
  type FormState,
  type SaveMode,
} from "./carouselSlideFormModel";

interface CarouselSlideFormProps {
  initial: CarouselSlide | null;
  regionId: string;
  canWrite: boolean;
}

export default function CarouselSlideForm({
  initial,
  regionId,
  canWrite,
}: CarouselSlideFormProps) {
  const { t, i18n } = useTranslation("adminCarousel");
  const { t: tComponents } = useTranslation("components");
  const { t: tCommon } = useTranslation("common");
  const { t: tErrors } = useTranslation("errors");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isCreate = initial === null;
  const [draftSlideId] = useState(() => crypto.randomUUID());
  const mediaEntityId = initial?.id ?? draftSlideId;
  const [incompleteTranslateOpen, setIncompleteTranslateOpen] = useState(false);

  const activitiesQuery = useQuery({
    queryKey: ["admin", "activities", regionId, "carousel"],
    queryFn: () =>
      adminService.getActivities({ region_id: regionId }),
    enabled: Boolean(regionId),
  });
  const { data: activities = [] } = activitiesQuery;

  const housesQuery = useQuery({
    queryKey: ["admin", "houses", regionId, "carousel"],
    queryFn: () => adminService.getHouses({ region_id: regionId }),
    enabled: Boolean(regionId),
  });
  const { data: houses = [] } = housesQuery;
  const housesLoadError = queryLoadFailure(housesQuery, tErrors("loadHouses"));

  const [form, setForm] = useState<FormState>(() =>
    initial ? formFromSlide(initial) : emptyFormState(),
  );
  const [baseline, setBaseline] = useState<FormState>(form);
  /** Picker is collapsed once an activity is linked; "Change" reopens it. */
  const [activityPickerOpen, setActivityPickerOpen] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [activitySearch, setActivitySearch] = useState("");
  const [copyLanguage, setCopyLanguage] = useState<SupportedLocale>(() =>
    resolveLocale(i18n.language),
  );

  const hasUnsavedChanges =
    canWrite && serializeFormState(form) !== serializeFormState(baseline);
  const { saveBarPhase, notifySaved } = useUnsavedFormGuard(hasUnsavedChanges);

  const handleRevert = () => {
    setForm(baseline);
    setErrors({});
    setActivitySearch("");
  };

  const hasImageOrActivity =
    Boolean(form.imagePath) || Boolean(form.activityId);
  const hasCategories = form.categories.length > 0;
  const hasAnyTitle = SUPPORTED_LOCALES.some((code) =>
    Boolean(form.title[code]?.trim()),
  );
  const readyExceptTranslations =
    hasImageOrActivity && hasCategories && hasAnyTitle;
  const defaultSaveMode: SaveMode = readyExceptTranslations
    ? "publish"
    : "draft";
  const secondarySaveMode: SaveMode =
    defaultSaveMode === "draft" ? "publish" : "draft";
  const saveModeLabel = (mode: SaveMode) =>
    mode === "draft" ? tCommon("saveBar.saveDraft") : tCommon("saveBar.save");
  const translateSourceLanguage =
    findAutoTranslateSourceLanguage(form.title, copyLanguage) ?? copyLanguage;

  const houseOptions: MultiSelectOption[] = useMemo(
    () => houses.map((row) => ({ value: row.id, label: row.name })),
    [houses],
  );

  const categoryOptions: MultiSelectOption<ActivityCategory>[] =
    ACTIVITY_TYPE_ORDER.map((cat) => ({
      value: cat,
      label: activityTypeLabel(cat),
    }));

  const selectedActivity = activities.find((a) => a.id === form.activityId);
  const activityCard = (() => {
    if (selectedActivity) {
      return {
        name: resolveCatalogTranslation(
          selectedActivity.name_translations,
          i18n.language,
        ),
        category: selectedActivity.category,
        ownerName: selectedActivity.owner_name,
      };
    }
    if (initial?.activity != null && initial.activity.id === form.activityId) {
      return {
        name: initial.activity.name,
        category: initial.activity.category,
        ownerName: null as string | null,
      };
    }
    return null;
  })();

  const filteredActivities = useMemo(() => {
    return activities.filter((row) => {
      const name = resolveCatalogTranslation(
        row.name_translations,
        i18n.language,
      );
      return matchesTextQuery(activitySearch, [
        name,
        row.owner_name,
        activityTypeLabel(row.category),
      ]);
    });
  }, [activities, activitySearch, i18n.language]);

  const selectActivity = (activityId: string | null) => {
    const activity = activityId
      ? activities.find((row) => row.id === activityId)
      : undefined;
    setForm((prev) => {
      const stillDefaultCategories =
        isCreate &&
        prev.categories.length === ACTIVITY_TYPE_ORDER.length &&
        ACTIVITY_TYPE_ORDER.every((cat) => prev.categories.includes(cat));
      return {
        ...prev,
        activityId,
        showActivityButton: Boolean(activityId),
        ignoreDistance: activityId ? prev.ignoreDistance : false,
        ...(stillDefaultCategories && activity
          ? { categories: [activity.category] }
          : {}),
      };
    });
    if (activityId) {
      setErrors((prev) => ({
        ...prev,
        photo: undefined,
        categories: undefined,
      }));
      setActivitySearch("");
    }
    setActivityPickerOpen(false);
  };

  const isCopyLocaleComplete = (code: SupportedLocale) => {
    if (!form.title[code]?.trim()) return false;
    const anyDescription = SUPPORTED_LOCALES.some((locale) =>
      Boolean(form.description[locale]?.trim()),
    );
    if (!anyDescription) return true;
    return Boolean(form.description[code]?.trim());
  };

  const saveMutation = useMutation({
    mutationFn: async ({
      mode,
      snapshot,
    }: {
      mode: SaveMode;
      snapshot: FormState;
    }) => {
      const fields = fieldsFromForm(snapshot, mode);

      if (initial === null) {
        return carouselSlideService.create({
          id: draftSlideId,
          region_id: regionId,
          ...fields,
        });
      }

      return carouselSlideService.replace(initial.id, fields);
    },
    onSuccess: async (slide, { mode }) => {
      await queryClient.invalidateQueries({
        queryKey: carouselSlideKeys.all,
      });
      toast.success(
        mode === "draft"
          ? t("toast.draftSaved")
          : isCreate
            ? t("toast.created")
            : t("toast.updated"),
      );
      notifySaved();
      if (isCreate) {
        // Stay on the form; replace so the URL picks up the new slide id.
        navigate(`/admin/carousel/${slide.id}`, { replace: true });
        return;
      }
      const next = formFromSlide(slide);
      setBaseline(next);
      setForm(next);
    },
    onError: (err) => {
      toastApiError(err, undefined, t("errors.saveFailed"));
    },
  });

  const autoTranslate = useCatalogAutoTranslate({
    sourceLanguage: translateSourceLanguage,
    nameTranslations: form.title,
    descriptionTranslations: form.description,
    requireDescription: false,
    onMissingName: () => {
      setErrors((prev) => ({
        ...prev,
        title: t("errors.titleRequiredDraft"),
      }));
      setIncompleteTranslateOpen(false);
    },
  });

  const save = (mode: SaveMode, snapshot: FormState) => {
    const result = validateSlideForm(
      snapshot,
      mode,
      {
        titleRequiredDraft: t("errors.titleRequiredDraft"),
        titleRequired: t("errors.titleRequired"),
        descriptionPartial: t("errors.descriptionPartial"),
        photoRequired: t("errors.photoRequired"),
        categoriesRequired: t("errors.categoriesRequired"),
        structuresRequired: t("errors.structuresRequired"),
      },
      {
        isCopyLocaleComplete,
        translateSourceLanguage,
      },
    );
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    if (result.incompleteTranslations) {
      setIncompleteTranslateOpen(true);
      return;
    }
    saveMutation.mutate({ mode, snapshot });
  };

  return (
    <>
      <form
        id={CAROUSEL_SLIDE_FORM_ID}
        className="flex max-w-6xl flex-col gap-6 pb-28"
        onSubmit={(e) => {
          e.preventDefault();
          save(defaultSaveMode, form);
        }}
      >
        <div className="grid items-start gap-6 lg:grid-cols-2">
          <div className="flex flex-col gap-6">
            <SectionCard
              title={t("form.sections.content")}
              description={t("form.sections.contentHint")}
              icon={Languages}
            >
              <div className="flex flex-col gap-5">
                <CatalogTranslatedField
                  values={form.title}
                  onChange={(title) => {
                    setErrors((prev) => ({ ...prev, title: undefined }));
                    setForm((prev) => ({ ...prev, title }));
                  }}
                  label={t("form.titleLabel")}
                  fieldAriaLabel={(lang) => `${t("form.titleLabel")} (${lang})`}
                  hideHeading
                  required
                  maxLength={CAROUSEL_SLIDE_TITLE_MAX_LENGTH}
                  error={errors.title}
                  selectedLanguage={copyLanguage}
                  onSelectedLanguageChange={setCopyLanguage}
                  isLocaleComplete={isCopyLocaleComplete}
                />

                <CatalogTranslatedField
                  values={form.description}
                  onChange={(description) => {
                    setErrors((prev) => ({ ...prev, description: undefined }));
                    setForm((prev) => ({ ...prev, description }));
                  }}
                  label={t("form.descriptionLabel")}
                  fieldAriaLabel={(lang) =>
                    `${t("form.descriptionLabel")} (${lang})`
                  }
                  hideHeading
                  multiline
                  requireAllLocales={false}
                  maxLength={CAROUSEL_SLIDE_DESCRIPTION_MAX_LENGTH}
                  rows={3}
                  inputClassName="min-h-[4.5rem]"
                  error={errors.description}
                  selectedLanguage={copyLanguage}
                  onSelectedLanguageChange={setCopyLanguage}
                  hideLanguageControls
                />
              </div>
            </SectionCard>

            {/* Visibility above Media so Safari's downward datetime picker
                has page content below instead of clipping past the viewport. */}
            <SectionCard
              title={t("form.sections.visibility")}
              description={t("form.sections.visibilityHint")}
              icon={CalendarClock}
            >
              <div className="flex flex-col gap-5">
                <Switch
                  checked={form.isEnabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      isEnabled: e.target.checked,
                    }))
                  }
                  disabled={!canWrite}
                  label={t("form.enabled")}
                  description={t("form.enabledHint")}
                />

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="min-w-0 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="carousel-starts-at">
                        {t("form.startsAt")}
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!canWrite || !form.startsAt}
                        onClick={() =>
                          setForm((prev) => ({ ...prev, startsAt: "" }))
                        }
                      >
                        {t("form.clearSchedule")}
                      </Button>
                    </div>
                    <Input
                      id="carousel-starts-at"
                      type="datetime-local"
                      value={form.startsAt}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          startsAt: e.target.value,
                        }))
                      }
                      disabled={!canWrite}
                    />
                  </div>
                  <div className="min-w-0 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="carousel-ends-at">
                        {t("form.endsAt")}
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!canWrite || !form.endsAt}
                        onClick={() =>
                          setForm((prev) => ({ ...prev, endsAt: "" }))
                        }
                      >
                        {t("form.clearSchedule")}
                      </Button>
                    </div>
                    <Input
                      id="carousel-ends-at"
                      type="datetime-local"
                      value={form.endsAt}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          endsAt: e.target.value,
                        }))
                      }
                      disabled={!canWrite}
                    />
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title={t("form.sections.media")}
              description={t("form.sections.mediaHint")}
              icon={ImageIcon}
            >
              {errors.photo ? (
                <Alert
                  variant="error"
                  className="mb-3"
                >
                  {errors.photo}
                </Alert>
              ) : null}
              <MediaUpload
                value={form.imagePath ?? undefined}
                onChange={(url) => {
                  setErrors((prev) => ({ ...prev, photo: undefined }));
                  setForm((prev) => ({ ...prev, imagePath: url ?? null }));
                }}
                entityType="carousel-slides"
                entityId={mediaEntityId}
                mediaType="image"
                size="lg"
                disabled={!canWrite}
                deleteFileOnRemove={false}
                crop={{
                  aspect: CAROUSEL_SLIDE_IMAGE_ASPECT,
                  description: t("form.photoCropDescription"),
                }}
              />
            </SectionCard>
          </div>

          <div className="flex flex-col gap-6">
            <SectionCard
              title={t("form.sections.activity")}
              description={t("form.sections.activityHint")}
              icon={Link2}
            >
              <div className="flex flex-col gap-4">
                {activityCard ? (
                  <div className="border-primary-200 bg-primary-50/60 flex items-start justify-between gap-3 rounded-lg border px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-surface-900 text-sm font-medium">
                        {activityCard.name}
                      </p>
                      <p className="text-surface-500 mt-0.5 text-[13px]">
                        {activityTypeLabel(activityCard.category)}
                        {activityCard.ownerName ? (
                          <>
                            {" · "}
                            {activityCard.ownerName}
                          </>
                        ) : null}
                      </p>
                    </div>
                    {canWrite ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-expanded={activityPickerOpen}
                          onClick={() => setActivityPickerOpen((open) => !open)}
                        >
                          {t("form.activityChange")}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t("form.activityClear")}
                          onClick={() => selectActivity(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {canWrite && (!activityCard || activityPickerOpen) ? (
                  <PickerPanel
                    searchPlaceholder={t("form.activitySearch")}
                    searchValue={activitySearch}
                    onSearchChange={setActivitySearch}
                    isLoading={activitiesQuery.isLoading}
                    loadError={queryLoadFailure(
                      activitiesQuery,
                      tErrors("loadActivities"),
                    )}
                    emptyMessage={
                      activitySearch.trim()
                        ? t("form.activityNoResults")
                        : t("form.activityListEmpty")
                    }
                    listVariant="divided"
                    items={filteredActivities.map((row) => {
                      const name = resolveCatalogTranslation(
                        row.name_translations,
                        i18n.language,
                      );
                      return {
                        id: row.id,
                        title: name,
                        subtitle: `${activityTypeLabel(row.category)} · ${row.owner_name}`,
                        detail: row.is_active
                          ? undefined
                          : t("form.activityInactive"),
                        selected: form.activityId === row.id,
                        onSelect: () => selectActivity(row.id),
                      };
                    })}
                  />
                ) : null}

                {form.activityId ? (
                  <div className="border-surface-200 flex flex-col gap-4 border-t pt-4">
                    <Switch
                      checked={form.showActivityButton}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          showActivityButton: e.target.checked,
                        }))
                      }
                      disabled={!canWrite}
                      label={t("form.showButton")}
                      description={t("form.showButtonHint")}
                    />
                    <Switch
                      checked={form.ignoreDistance}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          ignoreDistance: e.target.checked,
                        }))
                      }
                      disabled={!canWrite}
                      label={t("form.ignoreDistance")}
                      description={t("form.ignoreDistanceHint")}
                    />
                  </div>
                ) : null}
              </div>
            </SectionCard>

            <SectionCard
              title={t("form.sections.targeting")}
              description={t("form.sections.targetingHint")}
              icon={Target}
            >
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <MultiSelect
                    label={t("form.categoriesLabel")}
                    hint={t("form.categoriesHint")}
                    placeholderLabel={t("categories.select")}
                    removeChipLabel={(chipLabel) =>
                      tComponents("multiSelectRemoveChip", { label: chipLabel })
                    }
                    options={categoryOptions}
                    value={form.categories}
                    onChange={(categories) => {
                      setErrors((prev) => ({
                        ...prev,
                        categories: undefined,
                      }));
                      setForm((prev) => ({
                        ...prev,
                        categories,
                      }));
                    }}
                    className={
                      canWrite ? undefined : "pointer-events-none opacity-60"
                    }
                  />
                  {errors.categories ? (
                    <FieldError>{errors.categories}</FieldError>
                  ) : null}
                </div>

                <Switch
                  checked={form.targeting === "all"}
                  onChange={(e) => {
                    const showAll = e.target.checked;
                    setErrors((prev) => ({
                      ...prev,
                      structures: undefined,
                    }));
                    setForm((prev) => ({
                      ...prev,
                      targeting: showAll ? "all" : "selected",
                      houseIds: showAll ? [] : prev.houseIds,
                    }));
                  }}
                  disabled={!canWrite}
                  label={t("form.showInAllStructures")}
                  description={t("form.showInAllStructuresHint")}
                />

                {form.targeting === "selected" ? (
                  <div className="flex flex-col gap-1.5">
                    <MultiSelect
                      label={t("form.structuresLabel")}
                      hint={t("form.structuresHint")}
                      placeholderLabel={
                        housesQuery.isLoading
                          ? tCommon("loading")
                          : t("structures.all")
                      }
                      removeChipLabel={(chipLabel) =>
                        tComponents("multiSelectRemoveChip", {
                          label: chipLabel,
                        })
                      }
                      options={houseOptions}
                      value={form.houseIds}
                      onChange={(houseIds) => {
                        setErrors((prev) => ({
                          ...prev,
                          structures: undefined,
                        }));
                        setForm((prev) => ({ ...prev, houseIds }));
                      }}
                      className={
                        canWrite ? undefined : "pointer-events-none opacity-60"
                      }
                    />
                    {housesLoadError ? (
                      <QueryLoadError failure={housesLoadError} />
                    ) : null}
                    {errors.structures ? (
                      <FieldError>{errors.structures}</FieldError>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </SectionCard>
          </div>
        </div>
      </form>

      {canWrite ? (
        <SaveBar
          phase={saveBarPhase}
          isPending={saveMutation.isPending || autoTranslate.isPending}
          formId={CAROUSEL_SLIDE_FORM_ID}
          onRevert={handleRevert}
          submitLabel={saveModeLabel(defaultSaveMode)}
          secondaryAction={{
            label: saveModeLabel(secondarySaveMode),
            onClick: () => save(secondarySaveMode, form),
          }}
        />
      ) : null}

      <ConfirmModal
        isOpen={incompleteTranslateOpen}
        onClose={() => {
          if (!autoTranslate.isPending) {
            setIncompleteTranslateOpen(false);
          }
        }}
        onConfirm={() => {
          void (async () => {
            const result = await autoTranslate.execute(translateSourceLanguage);
            if (!result) return;
            const applied = applyAutoTranslateResult(
              form.title,
              form.description,
              result,
              { mergeDescriptions: true },
            );
            const next: FormState = {
              ...form,
              title: applied.nameTranslations,
              description: applied.descriptionTranslations,
            };
            setForm(next);
            setErrors({});
            setIncompleteTranslateOpen(false);
            save("publish", next);
          })();
        }}
        title={t("form.incompleteTranslationsTitle")}
        message={t("form.incompleteTranslationsBody")}
        confirmText={t("form.translate")}
        cancelText={tCommon("cancel")}
        closeLabel={tCommon("close")}
        isLoading={autoTranslate.isPending || saveMutation.isPending}
      />
    </>
  );
}
